import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import {
    initializeTagEditorCreate,
    initializeTagEditorEdit,
    updateTagTemplate,
    clearTagEditor,
    validateTagNameUnique,
    addTagQuestion,
} from "../../../actions/tagEditorActions";
import { getTag } from "../../../actions/tagActions";
import QuestionViewerExpandableHook from "../questions/QuestionViewerExpandableHook";
import ArgoReviewTemplateHook from "../../common/ArgoReviewTemplateHook";
import ArgoSelectExistingHook from "../../common/ArgoSelectExistingHook";
import ArgoToolbarHook from "../../common/ArgoToolbarHook";
import ArgoTextFieldHook from "../../common/ArgoTextFieldHook";
import * as validate from "../../../utilities/validate";
import * as TEMPLATE from "../../../constants/templateConstants";


function TagEditorHook({ id, createMode, save, cancel, width }) {
    // State
    const [openQuestionReview, setOpenQuestionReview] = useState(false);
    const [selectedQuestionInfoId, setSelectedQuestionInfoId] = useState("");
    const [selectedQuestionInfoKey, setSelectedQuestionInfoKey] = useState("");
    const [selectedQuestionInfoTemplate, setSelectedQuestionInfoTemplate] = useState("");
    const [nameErrorText, setNameErrorText] = useState(" ");
    const [toolbarErrorText, setToolbarErrorText] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [questionListRemoved, setQuestionListRemoved] = useState([]);
    // const [tagNameEditDialogOpen, setTagNameEditDialogOpen] = useState(false);
    const [originalName, setOriginalName] = useState("");
    const [initialized, setInitialized] = useState(false);

    // Redux
    const dispatch = useDispatch();
    const template = useSelector(state => state.tagEditor.template);
    const questionKeys = useSelector(state => state.tagEditor.questionKeys);
    const options = useSelector(state => state.tagEditor.options);
    const tagDataFromStore = useSelector(state => state.tag.hasOwnProperty(id) ? state.tag[id] : null);
    const tag = tagDataFromStore !== null ? tagDataFromStore : {};


    // Initialization Effect
    useEffect(() => {
        if (id) {
            setIsLoading(true);
            dispatch(getTag(id));
        } else {
            // For create mode, we don't need to depend on template
            dispatch(initializeTagEditorCreate());
        }

        return () => {
            dispatch(clearTagEditor());
        };
    }, [id, dispatch]); // Remove template dependency

    // Edit Mode Initialization Effect
    useEffect(() => {
        if (!id || !tagDataFromStore) return;
        
        const isTemplateLoaded = tagDataFromStore.id === id;

        if (isTemplateLoaded && !initialized) {
            // Set loading to false as soon as data is confirmed loaded
            setIsLoading(false);
            setOriginalName(tagDataFromStore.name);
            setInitialized(true);
            dispatch(initializeTagEditorEdit(tagDataFromStore));
        }
    }, [tagDataFromStore, id, initialized, dispatch]);

    // Error Checking Effect
    useEffect(() => {
        if (template) {
            const validationContext = {
                template: template,
                tag: tagDataFromStore,
                options: options
            };

            const nameError = validate.templateNameUnique(validationContext, "Tag Name");
            const toolbarError = nameError !== " " ? nameError : "";
            
            setNameErrorText(nameError);
            setToolbarErrorText(toolbarError);
        }
    }, [template, tagDataFromStore, options]);

    // Event Handlers
    const handleToolbarButtonClick = useCallback((event, fieldName, buttonTitle) => {
        switch (buttonTitle) {
            case "SAVE":
                const templateToSave = {
                    ...template,
                    questionKeys: [...questionKeys]
                };
                save(templateToSave, [...questionListRemoved]);
                break;
            case "CANCEL":
                cancel();
                break;
            case "BACK":
                setOpenQuestionReview(false);
                break;
            // no default
        }
        event.preventDefault();
    }, [template, questionKeys, questionListRemoved, save, cancel]);

    const handleNameChange = useCallback((event, fieldName) => {
        const value = event.target.value;
        const updatedTemplate = {
            ...template,
            [fieldName]: value,
            questionKeys: questionKeys
        };

        dispatch(updateTagTemplate(updatedTemplate));

        if (fieldName === "name") {
            dispatch(validateTagNameUnique(updatedTemplate.name));
        }
    }, [template, questionKeys, dispatch]);

    const handleTagTitleEditOnBlur = useCallback(() => {
        const isEdit = !!id;

        if (isEdit && !isLoading && originalName !== "") {
            if (template.name !== originalName) {
                // setTagNameEditDialogOpen(true);
            }
        }
    }, [id, isLoading, originalName, template.name]);

    // const handleCloseTagNameEditConfirm = useCallback(() => {
    //     setTagNameEditDialogOpen(false);
    // }, []);

    const addQuestionToTag = useCallback((questionTemplateKeyObj) => {
        const questionTemplateKey = questionTemplateKeyObj.templateKey;

        if (!questionKeys.includes(questionTemplateKey)) {
            dispatch(addTagQuestion(questionTemplateKey));
        }

        // Remove from questionListRemoved if it was there
        if (questionListRemoved.includes(questionTemplateKey)) {
            setQuestionListRemoved(prev => 
                prev.filter(item => item !== questionTemplateKey)
            );
        }
    }, [questionKeys, questionListRemoved, dispatch]);

    const handleQuestionViewerStateChange = useCallback((stateObj) => {
        if ('openQuestionReview' in stateObj) setOpenQuestionReview(stateObj.openQuestionReview);
        if ('selectedQuestionInfoId' in stateObj) setSelectedQuestionInfoId(stateObj.selectedQuestionInfoId);
        if ('selectedQuestionInfoKey' in stateObj) setSelectedQuestionInfoKey(stateObj.selectedQuestionInfoKey);
        if ('selectedQuestionInfoTemplate' in stateObj) setSelectedQuestionInfoTemplate(stateObj.selectedQuestionInfoTemplate);
    }, []);

    const handleQuestionRemoved = useCallback((questionKey) => {
        setQuestionListRemoved(prev => [...prev, questionKey]);
    }, []);

    // Render
    if (isLoading) {
        //TODO place a loading screen here
        return null;
    }

    if (openQuestionReview) {
        return <ArgoReviewTemplateHook
            templateType={TEMPLATE.TYPE.QUESTION}
            templateId={selectedQuestionInfoId}
            templateKey={selectedQuestionInfoKey}
            selectedTemplate={selectedQuestionInfoTemplate}
            handleToolbarButtonClickFunction={handleToolbarButtonClick}
            width={width}
        />;
    }

    return (
        createMode || tag.hasOwnProperty("questionKeys") ?
            <div id="TagEditor-container-outer-div" style={{height: "100%"}}>
                <ArgoToolbarHook
                    fileName="TagEditor"
                    fieldName="save-or-cancel"
                    toolbarErrorText={toolbarErrorText}
                    isValidating={options.isValidating}
                    onClick={handleToolbarButtonClick}
                    width={width}
                />
                <div id="TagEditor-height-div" style={{height: "50px"}}></div>

                <div id="TagEditor-container-inner-div" style={{height: "100%"}}>
                    <div id="TagEditor-container-div1" style={{justifyContent: "space-between"}}>
                        <div id="TagEditor-container-div2"
                             style={{flexGrow: "2", paddingLeft: "32px", paddingRight: "32px"}}>

                            <div id="TagEditor-spacer-div1" style={{height: "10px"}}>&nbsp;</div>

                            <div id="TagEditor-container-div3"
                                 style={{display: "flex", alignItems: "center", width: "100%"}}>
                                <ArgoTextFieldHook
                                    fileName="TagEditor"
                                    fieldName="name"
                                    label="Name"
                                    value={template.name}
                                    onChange={handleNameChange}
                                    onBlur={handleTagTitleEditOnBlur}
                                    maxLength={15}
                                    width="210px"
                                    errorText={nameErrorText}
                                    autoFocus={true}
                                />
                            </div>

                            <div style={{height: "20px"}}>&nbsp;</div>

                            {/*// ===========================================================================================================*/}
                            {/*// ARGO SELECT EXISTING QUESTION TO ADD TO TAG */}
                            {/*// ===========================================================================================================*/}

                            {!createMode ?
                                <div id="TagEditor-argo-select-existing-div" style={{paddingRight: "30px"}}>
                                    {tag.id !== null ? <ArgoSelectExistingHook
                                        mode={TEMPLATE.TYPE.QUESTION}
                                        editorName="TagEditor"
                                        onAddTemplate={addQuestionToTag}
                                        createMode={createMode}
                                        parentTemplate={template}
                                    /> : ""}
                                </div> : ""
                            }

                            <div style={{height: "20px"}}>&nbsp;</div>

                            {/*// ===========================================================================================================*/}
                            {/*// ASSIGNED QUESTIONS LIST */}
                            {/*// ===========================================================================================================*/}

                            {!createMode ?
                                <QuestionViewerExpandableHook
                                    parentName={"TagEditor"}
                                    parentType={TEMPLATE.TYPE.TAG}
                                    parentTemplate={tag}
                                    questionKeys={questionKeys.length ? questionKeys : tag.questionKeys}
                                    showIconButtonReorder={false}
                                    showIconButtonEdit={false}
                                    showIconButtonRemove={false}
                                    setStateCallback={handleQuestionViewerStateChange}
                                    setStateCallbackRemovedQuestionListUpdate={handleQuestionRemoved}
                                /> : ""
                            }

                            {/*// ===========================================================================================================*/}
                            {/*// CONFIRM CHANGE PROMPT */}
                            {/*// ===========================================================================================================*/}

                            {/*<ConfirmPromptHook open={tagNameEditDialogOpen}*/}
                            {/*                    title={"Changing the tag name may have unintended consequences."}*/}
                            {/*                    message={"Any questions that this tag is associated with in the system will now have a different meaning. " +*/}
                            {/*                   "Are you sure you want to do this?"}*/}
                            {/*                    ok={handleCloseTagNameEditConfirm}*/}
                            {/*                    cancel={handleCloseTagNameEditConfirm}*/}
                            {/*/>*/}
                        </div>
                    </div>
                </div>
            </div> : ""
    );
}

TagEditorHook.propTypes = {
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    createMode: PropTypes.bool,
    save: PropTypes.func,
    cancel: PropTypes.func,
    width: PropTypes.string
};

export default TagEditorHook;
